import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import StructuredPromptTemplate from './StructuredPromptTemplate';
import { AD } from './adsAgentTokens';
import {
  AD_TYPE_AUTO,
  STATUS_ASPECT_RATIO,
  isStatusAdType,
} from './adTypeOptions';
import {
  MAX_PRODUCT_IMAGES,
  productImageRejection,
  releaseProductImage,
  toProductImage,
} from './imageFiles';
import { ProductImage } from './adsGenerator.types';

// Port of whatsapp-console-frontend `src/pages/AdTemplates/TemplatePromptBar.js`
// for the public page: image only, free tier only, no credits.

export const MIN_PROMPT = 3;
// The public page always generates this many ads.
export const DEFAULT_TEMPLATE_COUNT = 3;
export const DEFAULT_ASPECT_RATIO = '3:4';

export type PromptSubmission = {
  prompt: string;
  templateCount: number;
  aspectRatio: string;
  adType: string;
  productImages: ProductImage[];
};

const SPARKLE_DEFS = [
  { x: '4%', y: 52, size: 14, color: AD.decorSoft, glyph: '+' },
  { x: '8%', y: 28, size: 11, color: AD.decorAccent, glyph: '✦' },
  { x: '11%', y: 90, size: 9, color: AD.decorTint, glyph: '+' },
  { x: '16%', y: 48, size: 8, color: AD.decorAccent, glyph: '✦' },
  { x: '2%', y: 110, size: 7, color: AD.decorTint, glyph: '+' },
  { x: '88%', y: 30, size: 14, color: AD.decorTint, glyph: '+' },
  { x: '93%', y: 70, size: 11, color: AD.decorAccent, glyph: '✦' },
  { x: '97%', y: 50, size: 9, color: AD.decorSoft, glyph: '+' },
  { x: '84%', y: 100, size: 8, color: AD.decorTint, glyph: '✦' },
  { x: '96%', y: 110, size: 7, color: AD.decorAccent, glyph: '+' },
  { x: '38%', y: 18, size: 8, color: AD.decorAccent, glyph: '+' },
  { x: '62%', y: 22, size: 7, color: AD.decorTint, glyph: '✦' },
];

const useStyles = makeStyles((theme) => ({
  '@keyframes sparklePulse': {
    '0%, 100%': { opacity: 0.35 },
    '50%': { opacity: 0.85 },
  },
  '@keyframes sparkleDrift': {
    '0%': { transform: 'translate(0, 0) rotate(0deg) scale(1)' },
    '25%': { transform: 'translate(4px, -6px) rotate(45deg) scale(1.12)' },
    '50%': { transform: 'translate(-3px, -10px) rotate(90deg) scale(0.92)' },
    '75%': { transform: 'translate(-5px, -4px) rotate(135deg) scale(1.08)' },
    '100%': { transform: 'translate(0, 0) rotate(180deg) scale(1)' },
  },
  '@keyframes sparkleBlink': {
    '0%, 100%': { opacity: 0.25 },
    '40%': { opacity: 1 },
    '55%': { opacity: 0.45 },
    '70%': { opacity: 0.95 },
  },
  '@keyframes badgeStarSpin': {
    '0%': { transform: 'rotate(0deg) scale(1)', opacity: 0.55 },
    '50%': { transform: 'rotate(180deg) scale(1.25)', opacity: 1 },
    '100%': { transform: 'rotate(360deg) scale(1)', opacity: 0.55 },
  },

  root: {
    position: 'relative',
    width: '100%',
    padding: '25px 25px 25px',
    background: AD.surfaceSunken,
    overflow: 'hidden',
    boxSizing: 'border-box',
    fontFamily: AD.fontFamily,
    [theme.breakpoints.down('xs')]: { padding: '28px 16px 24px' },
  },

  sparkle: {
    position: 'absolute',
    userSelect: 'none',
    pointerEvents: 'none',
    lineHeight: 1,
    fontStyle: 'normal',
    willChange: 'transform, opacity',
    animationName: '$sparkleBlink, $sparkleDrift',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite',
    animationDirection: 'normal, alternate',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: '$sparklePulse',
      animationDuration: '3s',
    },
  },

  badge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  badgePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 14px',
    borderRadius: AD.radiusLg,
    fontSize: 15,
    fontWeight: 700,
    color: AD.primary,
    textTransform: 'uppercase',
  },
  badgeStar: {
    fontSize: 11,
    color: AD.primary,
    display: 'inline-block',
    willChange: 'transform, opacity',
    animation: '$badgeStarSpin 4.5s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: '$sparklePulse 3s ease-in-out infinite',
    },
  },

  heading: {
    textAlign: 'center',
    fontSize: 36,
    fontWeight: 800,
    color: AD.text,
    lineHeight: 1.15,
    margin: '0 0 12px',
    [theme.breakpoints.down('xs')]: { fontSize: 24 },
  },

  sub: {
    textAlign: 'center',
    fontSize: 14,
    color: AD.textMuted,
    margin: '0 0 12px',
  },

  cardWrap: {
    maxWidth: 1080,
    margin: '0 auto',
  },

  note: {
    maxWidth: 1080,
    margin: '8px auto 0',
    fontSize: 12,
    color: AD.error,
    minHeight: 16,
    textAlign: 'left',
  },
}));

const TemplatePromptBar = (props: Props): React.ReactElement => {
  const { submitting, disabled, onSubmit } = props;
  const classes = useStyles();
  const [error, setError] = useState('');
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
  const [adType, setAdType] = useState(AD_TYPE_AUTO);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);

  const sparkles = useMemo(
    () =>
      SPARKLE_DEFS.map((s, i) => {
        const blink = 2.4 + (i % 4) * 0.5;
        const drift = 7 + (i % 5) * 1.6;
        const delay = i * 0.37;
        return {
          ...s,
          key: i,
          animDelay: `${delay.toFixed(2)}s, ${(delay * 0.6).toFixed(2)}s`,
          animDuration: `${blink.toFixed(2)}s, ${drift.toFixed(2)}s`,
        };
      }),
    [],
  );

  const locked = submitting || disabled;

  const addProductImages = (files: File[]) => {
    const room = MAX_PRODUCT_IMAGES - productImages.length;
    if (room <= 0) {
      setError(`You can add up to ${MAX_PRODUCT_IMAGES} product images`);
      return;
    }
    const accepted: ProductImage[] = [];
    let message =
      files.length > room
        ? `Only ${room} more image${room === 1 ? '' : 's'} can be added`
        : '';
    files.slice(0, room).forEach((file) => {
      const rejection = productImageRejection(file);
      if (rejection) {
        message = rejection;
        return;
      }
      accepted.push(toProductImage(file));
    });
    setError(message);
    setProductImages((current) => [...current, ...accepted]);
  };

  const removeProductImage = (id: string) =>
    setProductImages((current) => {
      const match = current.find((item) => item.id === id);
      if (match) {
        releaseProductImage(match);
      }
      return current.filter((item) => item.id !== id);
    });

  const effectiveAspectRatio = isStatusAdType(adType)
    ? STATUS_ASPECT_RATIO
    : aspectRatio;

  const handleAdTypeChange = (next: string) => {
    setAdType(next);
    if (isStatusAdType(next)) {
      setAspectRatio(STATUS_ASPECT_RATIO);
    }
  };

  const handleSubmit = (prompt: string) => {
    if (locked) {
      return;
    }
    if (!prompt || prompt.trim().length < MIN_PROMPT) {
      setError('Please fill in at least one field to describe your business');
      return;
    }
    setError('');
    onSubmit({
      prompt: prompt.trim(),
      templateCount: DEFAULT_TEMPLATE_COUNT,
      aspectRatio: effectiveAspectRatio,
      adType,
      productImages,
    });
  };

  return (
    <div className={classes.root}>
      {sparkles.map((s) => (
        <span
          key={s.key}
          className={classes.sparkle}
          style={{
            left: s.x,
            top: s.y,
            fontSize: s.size,
            color: s.color,
            animationDelay: s.animDelay,
            animationDuration: s.animDuration,
          }}
        >
          {s.glyph}
        </span>
      ))}

      <div className={classes.badge}>
        <span className={classes.badgePill}>
          <span className={classes.badgeStar}>✦</span>
          AI-POWERED
        </span>
      </div>

      <h2 className={classes.heading}>Ready-to-run ads in minutes</h2>

      <p className={classes.sub}>
        Describe your product or add a link to generate ads with AI.
      </p>

      <div className={classes.cardWrap}>
        <StructuredPromptTemplate
          submitting={submitting}
          disabled={locked}
          aspectRatio={effectiveAspectRatio}
          adType={adType}
          productImages={productImages}
          onAdTypeChange={handleAdTypeChange}
          onAddProductImages={addProductImages}
          onRemoveProductImage={removeProductImage}
          onAspectRatioChange={setAspectRatio}
          onSubmit={handleSubmit}
          onError={setError}
        />
      </div>

      {error ? <p className={classes.note}>{error}</p> : null}
    </div>
  );
};

interface Props {
  submitting: boolean;
  disabled: boolean;
  onSubmit: (submission: PromptSubmission) => void;
}

export default TemplatePromptBar;
